/**
 * The maps the then-and-now lens can show, one per place, chosen by where
 * the map's centre is. Denmark is the self-hosted 1842-1899 survey sheets
 * (see App.tsx `histStyle`); Kristiania is one georeferenced sheet from
 * Kartverket's historical archive, served live by their WMS, which answers
 * CORS-open in EPSG:3857 with `layers=georefererte&id=<kart>`. The id was
 * found through ws.geonorge.no/historiske-kart/v3 on 2 Sep 2026 among 92
 * georeferenced 19th-century sheets over central Oslo: nr 63, "Kart over
 * Kristiania" 1868 at 1:5000, covering 10.70-10.81°E × 59.89-59.95°N, the
 * whole city of the walks. Read by App.tsx for the lens button and style.
 */
import type { StyleSpecification } from "maplibre-gl";

export interface PastLayer {
  tiles: string[];
  bounds: [number, number, number, number];
  minzoom: number;
  maxzoom: number;
  /**
   * The year this sheet depicts, where the stack is a series rather than a
   * coverage fallback. Only Stockholm has one, and `pastSheetView` uses it to
   * let the timeline choose. A stack whose layers carry no year behaves
   * exactly as before: all of them, in the order they are listed.
   */
  year?: number;
}

export interface PastSheet {
  id: string;
  /**
   * Set when the service stopped drawing and the lens must not offer it: an
   * empty disc reads as a broken app. The sheet stays here with its
   * permission and its boxes, so switching it back on is one line.
   */
  offline?: string;
  /**
   * Where the credit links. Some licences ask for the link and not just the
   * name: the National Library of Scotland's CC BY 3.0 wants the attribution
   * "together with a link to our website".
   */
  creditUrl?: string;
  /** Printed on the disc: one year, or a range when the stack spans two. */
  label: string;
  /** The licence line under the disc. */
  credit: string;
  /** Union of the layers' bounds; the button appears inside it. */
  bounds: [number, number, number, number];
  /**
   * Coarse first, fine last, drawn in that order. A catalogue bbox is the
   * envelope of a rotated sheet, and the sheet itself may cover half of it
   * (Kristiania 1868 stops at Bislett; Marienlyst was bare paper), so a
   * wider, coarser sheet underneath is what keeps the disc from going blank.
   */
  layers: PastLayer[];
}

const KARTVERKET_WMS = "https://wms.geonorge.no/skwms1/wms.historiskekart";

/**
 * One georeferenced sheet from Kartverket's archive. minzoom 8, not the
 * sheet's own scale: the WMS renders any bbox, and a lens that goes blank
 * as soon as the map is zoomed out past the town is a lens that looks broken.
 */
function kartverketLayer(kart: number, bounds: PastLayer["bounds"]): PastLayer {
  return {
    tiles: [`${KARTVERKET_WMS}?service=WMS&version=1.3.0&request=GetMap&layers=georefererte&id=${kart}&styles=&crs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256&format=image/png&transparent=true`],
    bounds,
    minzoom: 8,
    maxzoom: 18,
  };
}

function helsinkiLayer(layer: string, bounds: PastLayer["bounds"]): PastLayer {
  return {
    tiles: [`https://kartta.hel.fi/ws/geoserver/avoindata/wms?service=WMS&version=1.3.0&request=GetMap&layers=avoindata:${layer}&styles=&crs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256&format=image/png&transparent=true`],
    bounds,
    minzoom: 9,
    maxzoom: 18,
  };
}

const STHLM_WMS = "https://kartor.stockholm.se/bios/wms/app/dpmap/cust_sth/utbf/web/WMS_UTBF_";

/**
 * Stockholm's Baggis WMS, one service per year. It must be asked in WMS 1.1.1
 * with `srs=`: 1.3.0 with `crs=` answers 200 with a zero-byte body and a layer
 * that never draws.
 *
 * Do not add `tiled=true`, whatever the city's Baggis notice says about the
 * image cache. That cache is keyed on its own grid in SWEREF 99 18 00, and a
 * Web Mercator tile bbox does not land on it, so the service answers
 * `<ServiceException code="InvalidTile">` — as HTTP 200, 239 bytes, content
 * type `application/vnd.ogc.se_xml`. It is fast, because an error page is
 * fast; the lens draws blank paper and the console says the image could not
 * be decoded. Measured 14 Sep 2026, after taking those 239 bytes for a cache
 * hit and calling it ten times quicker.
 */
function stockholmLayer(sti: string, year: number, layer: string, bounds: PastLayer["bounds"]): PastLayer {
  return {
    tiles: [`${STHLM_WMS}${sti}?service=WMS&version=1.1.1&request=GetMap&layers=${layer}&styles=&srs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256&format=image/png&transparent=true`],
    bounds,
    minzoom: 9,
    maxzoom: 18,
    year,
  };
}

/**
 * A plain WMS layer, for the services that answer any bbox in Web Mercator.
 * Germany's five all do; each was asked for a tile over its own cities on
 * 8 Sep 2026 before it went in here.
 */
function wmsLayer(url: string, layer: string, bounds: PastLayer["bounds"], minzoom = 8): PastLayer {
  return {
    tiles: [`${url}?service=WMS&version=1.3.0&request=GetMap&layers=${layer}&styles=&crs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256&format=image/png&transparent=true`],
    bounds,
    minzoom,
    maxzoom: 18,
  };
}

/**
 * The National Library of Scotland's Historic Maps API, hosted on MapTiler
 * Cloud. Chris Fleet, Map Curator, gave permission on 17 Sep 2026: CC BY 3.0,
 * and the credit has to be 'Courtesy of the National Library of Scotland' with
 * a link to maps.nls.uk. The free tier is 100 000 tile requests a month for
 * non-commercial use, and an opened lens is about a dozen tiles.
 *
 * The key is public by design, as every browser-side map key is; what protects
 * it is the allowed-origins list on the key itself.
 */
const NLS_KEY = import.meta.env.VITE_MAPTILER_KEY as string | undefined;
const NLS_CREDIT = "Courtesy of the National Library of Scotland, maps.nls.uk";

function nlsLayer(tileset: string, ext: "jpg" | "png", bounds: PastLayer["bounds"], minzoom: number, maxzoom: number): PastLayer {
  return {
    tiles: [`https://api.maptiler.com/tiles/${tileset}/{z}/{x}/{y}.${ext}?key=${NLS_KEY ?? ""}`],
    bounds,
    minzoom,
    maxzoom,
  };
}

const NRW_WMS = "https://www.wms.nrw.de/geobasis/wms_nw_uraufnahme";
const SCHMETTAU_WMS = "https://isk.geobasis-bb.de/mapproxy/schmettau/service/wms";
const STRAUBE_WMS = "https://gdi.berlin.de/services/wms/straubeplan";
const SH_WMS = "https://service.gdi-sh.de/WMS_SH_FD_Chronologen";
const SN_WMS = "https://geodienste.sachsen.de/wms_geosn_hist/guest";

function union(layers: PastLayer[]): PastSheet["bounds"] {
  return [
    Math.min(...layers.map(l => l.bounds[0])), Math.min(...layers.map(l => l.bounds[1])),
    Math.max(...layers.map(l => l.bounds[2])), Math.max(...layers.map(l => l.bounds[3])),
  ];
}

function kartverketSheet(id: string, label: string, names: string, layers: PastLayer[]): PastSheet {
  return { id, label, credit: `${names}. Kartverket, historiske kart, CC BY 4.0`, bounds: union(layers), layers };
}

export const PAST_SHEETS: PastSheet[] = [
  // Germany, five services, each measured on 8 Sep 2026 for the three things
  // the lens needs: a tile in EPSG:3857, an Access-Control-Allow-Origin that
  // lets www.fornland.com in, and a licence that names itself. Coverage was
  // sampled over thirty cities, all of them served. They stand first in this
  // list because two of the boxes reach into ground another sheet owns:
  // Schmettau's swallows Berlin and the north of Saxony, and the Danish
  // survey's envelope reaches down to 54,3°N, which is Kiel.
  //
  // Berlin is the one stack: Schmettau's 1:50 000 survey of 1767-87 under
  // Straube's 1:4 000 city plan of 1910, which stops at the historic centre.
  {
    id: "de-berlin",
    label: "1767–1910",
    credit: "Schmettausches Kartenwerk 1767-1787 og Straubes Übersichtsplan von Berlin 1910. © Staatsbibliothek zu Berlin – Preußischer Kulturbesitz, dl-de/by-2-0, og Geoportal Berlin, dl-de/zero-2-0",
    bounds: [13.294, 52.474, 13.499, 52.579],
    layers: [
      wmsLayer(SCHMETTAU_WMS, "schmettau", [13.294, 52.474, 13.499, 52.579]),
      wmsLayer(STRAUBE_WMS, "straubeplan", [13.294, 52.474, 13.499, 52.579]),
    ],
  },
  // Saxony's Messtischblätter carry their own year in the metadata layer, and
  // it is not one year: sampled on a quarter-degree grid, 34 sheets between
  // 1908 and 1944, most of them from the 1930s.
  {
    id: "de-sachsen",
    label: "1908–1944",
    credit: "Messtischblätter vor 1945. Quelle: GeoSN, dl-de/by-2-0",
    bounds: [11.87, 50.17, 15.05, 51.69],
    layers: [wmsLayer(SN_WMS, "messtischblatt_vor_1945", [11.87, 50.17, 15.05, 51.69], 7)],
  },
  {
    id: "de-brandenburg",
    label: "1767–1787",
    credit: "Schmettausches Kartenwerk 1767-1787. © Staatsbibliothek zu Berlin – Preußischer Kulturbesitz, dl-de/by-2-0",
    bounds: [11.15, 51.36, 15.10, 53.56],
    layers: [wmsLayer(SCHMETTAU_WMS, "schmettau", [11.15, 51.36, 15.10, 53.56], 7)],
  },
  {
    id: "de-nrw",
    label: "1836–1850",
    credit: "Uraufnahme 1836-1850. Geobasis NRW, dl-de/zero-2-0",
    bounds: [5.87, 50.32, 9.46, 52.53],
    layers: [wmsLayer(NRW_WMS, "nw_uraufnahme_rw", [5.87, 50.32, 9.46, 52.53], 7)],
  },
  // Schleswig-Holstein publishes four eras and the layer names are the
  // numbers 0 to 3, oldest last. Its sheets do not stop at the Land border:
  // asked over eight places on 8 Sep 2026, they cover Hamburg, Altona,
  // Wandsbek, Lübeck and Fehmarn, and answer blank over Sønderborg and Rødby,
  // so the box stops at the Danish border and the Danish survey keeps the
  // ground north of it.
  {
    id: "de-sh",
    label: "1878–1880",
    credit: "TK25 Chronologen 1878-1880. © GeoBasis-DE/LVermGeo SH/CC BY 4.0",
    bounds: [7.87, 53.36, 11.31, 54.87],
    layers: [wmsLayer(SH_WMS, "3", [7.87, 53.36, 11.31, 54.87], 7)],
  },
  {
    id: "dk-hmb",
    label: "1842–1899",
    credit: "Indeholder data fra Styrelsen for Dataforsyning og Infrastruktur, Danske målebordsblade",
    bounds: [7.5, 54.3, 15.4, 58.0],
    layers: [{ tiles: ["https://thormartinbaerug.com/evald/tiles/{z}/{x}/{y}.png"], bounds: [7.5, 54.3, 15.4, 58.0], minzoom: 7, maxzoom: 14 }],
  },
  // Kristiania: the 1901 city map at 1:10000 (nr 77) under the two 1868
  // sheets at 1:5000 (nr 63, west and east). Ids from the catalogue query of
  // 2 Sep 2026; each read in the browser before it went in.
  kartverketSheet("no-oslo", "1868–1901", "Kart over Kristiania 1868 (Kristiania amt nr 63) og 1901 (nr 77)", [
    kartverketLayer(1536, [10.663, 59.883, 10.837, 59.963]),
    kartverketLayer(1436, [10.701, 59.892, 10.792, 59.93]),
    kartverketLayer(1437, [10.701, 59.891, 10.814, 59.951]),
  ]),
  // The other Norwegian towns, same catalogue, same reading. Trondheim's 1909
  // sheet at 1:5000 (id 10555) is a faint scan; the 1904 survey at 1:3000 is
  // the legible one.
  kartverketSheet("no-bergen", "1883", "Kart over Bergen 1883, Bergen by nr 22-1", [kartverketLayer(556, [5.289, 60.374, 5.369, 60.424])]),
  kartverketSheet("no-trondheim", "1904", "Oversigtskart over Trondhjem 1904, Søndre Trondhjems amt nr 103-3", [kartverketLayer(2709, [10.341, 63.419, 10.407, 63.443])]),
  kartverketSheet("no-stavanger", "1870", "Kart over Stavanger By 1870, Stavanger amt nr 13", [kartverketLayer(2527, [5.701, 58.952, 5.775, 58.994])]),
  kartverketSheet("no-tromso", "1903", "Kart over Tromsø 1903, Tromsø amt nr 17", [kartverketLayer(10559, [18.937, 69.63, 18.979, 69.676])]),
  kartverketSheet("no-kristiansand", "1894", "Kart over Christiansand med Lahelle 1894, Lister og Mandals amt nr 48", [kartverketLayer(1794, [7.974, 58.135, 8.03, 58.165])]),
  kartverketSheet("no-drammen", "1906", "Kart over Drammen 1906, Buskerud amt nr 50", [kartverketLayer(6658, [10.148, 59.712, 10.27, 59.767])]),
  // Stockholm: the city's own Baggis WMS, one service per year. Asked on
  // 3 Sep 2026 and again on the 14th, because the service's AccessConstraints
  // is a blanket "får inte användas … utan särskilt medgivande" — which is the
  // default for anyone who has not asked. Kart- och geodataservice took it to
  // the information owner and answered that handing out the WMS is fine, with
  // a screenshot of this very lens over Bergen in the thread, so the use and
  // the credit line were both in front of them.
  //
  // Yanjing Jia, Kart- och geodataservice, sendte hele listen 21. september
  // 2026, og Stockholms stad har givet tilladelse. Femogtyve tjenester, én
  // pr. årtal fra 1625 til 1996, alle målt samme dag: https svarer, CORS er
  // `*`, EPSG:3857 er blandt 46 SRS, og hver leverer et rigtigt PNG.
  //
  // Boksene er svøbt på et 0,04° × 0,02°-gitter, fordi kataloget erklærer
  // den samme boks for alle femogtyve og den er større end papiret. To
  // familier kom ud af det: bykernebladene dækker 2 til 11 celler, og
  // byomfattende blade 42 til 116. 1625 er Gamla stan alene; fra 1934 rækker
  // papiret til Bromma og Solna.
  //
  // Rækkefølgen her er efter årstal, ikke efter dækning. `pastSheetView`
  // sorterer selv, fordi hvilket blad der skal ligge øverst afhænger af hvad
  // læseren har valgt på tidslinjen.
  {
    id: "se-stockholm",
    // Blank for every year from 30 Sep 2026 and drawing again on 7 Oct: all
    // twenty-five answered 78-187 kB with Access-Control-Allow-Origin * when
    // asked as the lens asks. Put back after Kart- och geodataservice's reply.
    label: "1625–1996",
    credit: "Stockholmskartor 1625–1996. Stockholms stad, Stadsbyggnadskontoret",
    bounds: [17.76, 59.19, 18.28, 59.45],
    layers: [
      stockholmLayer("1625", 1625, "p_1200000", [18.04, 59.31, 18.12, 59.33]),
      stockholmLayer("1640", 1640, "p_1200010", [18.04, 59.31, 18.12, 59.35]),
      stockholmLayer("1642", 1642, "p_1200001", [18.04, 59.29, 18.12, 59.35]),
      stockholmLayer("1702", 1702, "p_1200002", [18, 59.29, 18.12, 59.37]),
      stockholmLayer("1733", 1733, "p_1200003", [18, 59.29, 18.12, 59.37]),
      stockholmLayer("1751", 1751, "p_1200004", [18, 59.29, 18.12, 59.37]),
      stockholmLayer("1805", 1805, "p_1200005", [18, 59.31, 18.16, 59.35]),
      stockholmLayer("1817", 1817, "p_1200083", [17.84, 59.27, 18.24, 59.41]),
      stockholmLayer("1828", 1828, "p_1200006", [18.04, 59.29, 18.12, 59.35]),
      stockholmLayer("1855", 1855, "p_1200020", [18, 59.31, 18.12, 59.35]),
      stockholmLayer("1863", 1863, "p_1200007", [18, 59.31, 18.12, 59.35]),
      stockholmLayer("1885", 1885, "p_1200008", [18, 59.31, 18.12, 59.35]),
      stockholmLayer("1899", 1899, "p_1200009", [18, 59.29, 18.12, 59.35]),
      stockholmLayer("1909", 1909, "p_1200012", [18, 59.29, 18.12, 59.35]),
      stockholmLayer("1913", 1913, "p_1200013", [18, 59.31, 18.12, 59.35]),
      stockholmLayer("1918_21", 1918, "p_1200082", [17.84, 59.21, 18.16, 59.37]),
      stockholmLayer("1930", 1930, "p_1200015", [18, 59.29, 18.12, 59.35]),
      stockholmLayer("1934", 1934, "p_1200016", [17.88, 59.23, 18.24, 59.37]),
      stockholmLayer("1940", 1940, "p_1200017", [18, 59.31, 18.12, 59.37]),
      stockholmLayer("1944", 1944, "p_1200021", [17.88, 59.23, 18.2, 59.37]),
      stockholmLayer("1954", 1954, "p_1200022", [17.76, 59.23, 18.2, 59.45]),
      stockholmLayer("1956", 1956, "p_1200018", [17.8, 59.23, 18.16, 59.39]),
      stockholmLayer("1969_72", 1969, "p_1200080", [17.76, 59.19, 18.28, 59.45]),
      stockholmLayer("1976", 1976, "p_1200019", [17.8, 59.23, 18.16, 59.41]),
      stockholmLayer("1996", 1996, "p_1200081", [17.76, 59.19, 18.24, 59.45]),
    ],
  },
  // The British Isles, from the National Library of Scotland. Measured on a
  // 0,3-degree grid over the contested corner on 17 Sep 2026, and the two
  // sheets never carry paper in the same place: the Ordnance Survey layer is
  // blank over all of Ireland, and Bartholomew's is blank over all of Britain.
  // So the only question is which box claims a point, and Ireland has to come
  // first, because the OS box reaches to 7,79° W and would otherwise offer a
  // blank disc over Belfast and Derry.
  //
  // Ireland is two boxes for the same reason Iceland is: one rectangle that
  // held the island would also hold Kintyre and Islay, where Bartholomew stops
  // and the OS sheets do not. The northern box stops at 5,95° W, west of
  // Kintyre; the southern one stops at 55,1° N, south of it.
  ...(NLS_KEY ? ([["ie-south", [-10.7, 51.3, -5.36, 55.1]], ["ie-north", [-10.7, 55.1, -5.95, 55.45]]] as [string, PastLayer["bounds"]][]).map(([id, bounds]): PastSheet => ({
    id,
    label: "1940",
    credit: NLS_CREDIT,
    creditUrl: "https://maps.nls.uk",
    bounds,
    layers: [nlsLayer("uk-baire250k1940", "png", bounds, 5, 12)],
  })) : []),
  // Great Britain is the four-scale 1900s layer rather than the six-inch alone,
  // although the six-inch is what the curator pointed at. It *is* the six-inch
  // at the zooms where the six-inch exists, and it carries the quarter-inch and
  // one-inch underneath, which is what puts paper over the Isle of Man: the OS
  // County Series never covered it, and the six-inch layer answers blank there
  // at every zoom.
  //
  // London's five-foot plan goes on top, the way Straube's plan sits over
  // Schmettau in Berlin. It is the most detailed mapping of the city there is,
  // down to the divisions between terraced houses and the fire hydrants.
  ...(NLS_KEY ? [{
    id: "gb-os",
    label: "1885–1913",
    credit: NLS_CREDIT,
    creditUrl: "https://maps.nls.uk",
    bounds: [-7.793, 49.853, 1.79, 60.894] as PastSheet["bounds"],
    layers: [
      nlsLayer("uk-osgb1888", "jpg", [-7.793, 49.853, 1.79, 60.894], 5, 17),
      nlsLayer("uk-oslondon1k1893", "jpg", [-0.371, 51.348, 0.105, 51.616], 9, 20),
    ],
  }] : []),
  // Helsinki: the city's own open WMS (kartta.hel.fi, CC BY 4.0, CORS open,
  // EPSG:3857). The 1870 Senate sheet covers the whole municipality; the
  // 1909 city map with street names and plot numbers covers the centre, so
  // it goes on top. Coverage sampled on a grid on 2 Sep 2026.
  {
    id: "fi-helsinki",
    label: "1870–1909",
    credit: "Senaatinkartta 1870 ja Kartta Helsingin kaupungista 1909. Helsingin kaupunki, Kaupunkimittauspalvelut / Kaupunginarkisto, CC BY 4.0",
    bounds: [24.765, 60.008, 25.29, 60.322],
    layers: [
      helsinkiLayer("Senaatinkartta_1870", [24.765, 60.008, 25.29, 60.322]),
      helsinkiLayer("Kartta_Helsingin_kaupungista_ja_sen_alueista_1909", [24.85, 60.14, 25.0, 60.24]),
    ],
  },
  // Amsterdam: Publieke Werken 1:1000 sheets of 1909 over the Loman atlas of
  // 1876, as Web Mercator tiles from the Amsterdam Time Machine (CLARIAH /
  // Huygens), CORS open, CC0 where no rights holder remains. The city's own
  // host carries the same 1909 tiles but sends no CORS header, so it cannot
  // feed a WebGL map. Coverage sampled on a grid on 2 Sep 2026.
  {
    id: "nl-amsterdam",
    label: "1876–1909",
    credit: "Publieke Werken 1909 en Loman-atlas 1876. Amsterdam Time Machine / Stadsarchief Amsterdam, CC0",
    bounds: [4.84, 52.335, 4.99, 52.43],
    layers: [
      { tiles: ["https://images.huygens.knaw.nl/webmapper/maps/loman/{z}/{x}/{y}.jpeg"], bounds: [4.84, 52.335, 4.99, 52.43], minzoom: 11, maxzoom: 18 },
      { tiles: ["https://images.huygens.knaw.nl/webmapper/maps/pw-1909/{z}/{x}/{y}.png"], bounds: [4.84, 52.335, 4.99, 52.43], minzoom: 11, maxzoom: 18 },
    ],
  },
  // Iceland: the Danish General Staff's 1:50 000 sheets, 1900-1940, served by
  // Náttúrufræðistofnun's MapCache in EPSG:3857, CORS open, CC BY 4.0. The
  // capabilities box is the whole island; the sheets are not. Sampled on a
  // one-degree grid on 2 Sep 2026: the west from Reykjanes to the Westfjords,
  // and a strip of the south coast. Two entries, one per covered region, so
  // the button does not offer paper over the east.
  ...([["is-west", [-24.6, 63.8, -19.5, 66.6]], ["is-south", [-20.5, 63.3, -16.0, 64.2]]] as [string, PastLayer["bounds"]][]).map(([id, bounds]): PastSheet => ({
    id,
    label: "1900–1940",
    credit: "Herforingjaráðskort Dana 1:50.000, 1900-1940. Landmælingar Íslands / Náttúrufræðistofnun, CC BY 4.0",
    bounds,
    layers: [{
      tiles: ["https://gis.natt.is/mapcache/web-mercator?service=WMS&version=1.3.0&request=GetMap&layers=LMI_raster:Atlas_50&styles=&crs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256&format=image/png&transparent=true"],
      bounds, minzoom: 7, maxzoom: 16,
    }],
  })),
];

/** The sheet under a point, or null where the lens has nothing to show. */
/**
 * The earliest year a sheet depicts, read off the front of its label.
 *
 * Every label starts with a four-digit year, alone or as the start of a
 * range, and pastSheets.test.ts walks the whole list to keep it that way.
 * The timeline uses it to stop offering an 1868 town plan to a reader
 * standing in the Bronze Age.
 */
export function sheetFromYear(sheet: PastSheet): number {
  const m = sheet.label.match(/^(\d{4})/);
  return m ? Number(m[1]) : 0;
}

export function pastSheetAt(lng: number, lat: number): PastSheet | null {
  return PAST_SHEETS.find(s => !s.offline && lng >= s.bounds[0] && lng <= s.bounds[2] && lat >= s.bounds[1] && lat <= s.bounds[3]) ?? null;
}

/** A complete style for the lens map: paper, then the sheets, coarse to fine. */
export function pastSheetStyle(sheet: PastSheet, valgte?: PastLayer[]): StyleSpecification {
  const sources: StyleSpecification["sources"] = {};
  const layers: StyleSpecification["layers"] = [{ id: "past-paper", type: "background", paint: { "background-color": "#eee7d8" } }];
  (valgte ?? sheet.layers).forEach((l, i) => {
    sources[`past-${i}`] = { type: "raster", tiles: l.tiles, tileSize: 256, minzoom: l.minzoom, maxzoom: l.maxzoom, bounds: l.bounds, attribution: sheet.credit };
    layers.push({ id: `past-${i}`, type: "raster", source: `past-${i}` });
  });
  return { version: 8, sources, layers };
}

/** Bredden gange højden af en boks. Bruges til at lægge det brede nederst. */
function areal(b: PastLayer["bounds"]): number {
  return (b[2] - b[0]) * (b[3] - b[1]);
}

/**
 * Hvad linsen skal vise, og hvad der skal stå på skiven.
 *
 * Et blad med `year` hører til en serie og ikke til en dækningsstak. Stockholm
 * har femogtyve, og alle femogtyve i én stak ville være værre end tre: stilen
 * tegner det sidste lag øverst, så en læser over Gamla stan ville få 1996.
 *
 * Reglen har to tilfælde og ét princip, nemlig at det mest relevante blad
 * ligger øverst og at skiven aldrig går blank:
 *
 * - Uden et årstal på tidslinjen er det ældste blad det mest relevante. Det
 *   er hvad linsen er til for, og det er også hvad de tre blade gjorde før.
 * - Med et årstal er det nyeste blad *til og med* det år det mest relevante.
 *   Vælger læseren 1900, kommer 1899 øverst. Der vises aldrig et kort fra
 *   efter det år der er spurgt om.
 *
 * Under det ligger op til to bredere blade blandt de samme kandidater,
 * bredeste nederst, så papiret rækker ud over det øverste blads kant. Det er
 * samme greb som de tre blade brugte, hvor 1817 lå under 1640. Loftet på tre
 * er der fordi hvert lag er en WMS-kilde, og femogtyve ville være
 * femogtyve gange så mange kald mod en kommunes server for hver åbnet linse.
 *
 * En stak hvis blade ikke bærer `year` går uforandret igennem.
 */
export function pastSheetView(sheet: PastSheet, aar: number | null): { style: StyleSpecification; label: string } {
  const medAar = sheet.layers.filter((l) => l.year != null);
  if (medAar.length === 0) return { style: pastSheetStyle(sheet), label: sheet.label };

  const kandidater = aar == null ? medAar : medAar.filter((l) => l.year! <= aar);
  if (kandidater.length === 0) return { style: pastSheetStyle(sheet, []), label: sheet.label };

  const øverst = aar == null
    ? kandidater.reduce((a, b) => (b.year! < a.year! ? b : a))
    : kandidater.reduce((a, b) => (b.year! > a.year! ? b : a));

  /**
   * Reserven vælges efter tid og ikke efter bredde.
   *
   * Først var reglen «de to bredeste», og den lagde 1996 under 1625: en læser
   * over Djurgården, hvor 1625 intet papir har, fik et moderne kort i en
   * linse der hedder dengang. Målt på forhåndsvisningen 21. september 2026.
   *
   * Nu er reserven en stige, og hvert trin skal fordoble dækningen. Uden det
   * kravler den: 1640 er kun én celle bredere end 1625 og tilføjer intet, så
   * de tre pladser gik til 1640, 1642 og 1702, og Bromma stod uden papir.
   * Med fordoblingskravet bliver stigen 1625, 1642, 1702, 1817, så Gamla
   * stan får 1625, Kungsholmen 1642, Djurgården 1702 og Bromma 1817 — det
   * ældste kort der har papir hvert sted. Fire lag og ikke tre, fordi det
   * fjerde er det der holder forstæderne inde.
   *
   * Ved hvert trin tages det ældste blad der er bredt nok, for det er hele
   * pointen med linsen.
   */
  const under: PastLayer[] = [];
  let bredest = areal(øverst.bounds);
  while (under.length < 3) {
    const næste = kandidater
      .filter((l) => l !== øverst && !under.includes(l) && areal(l.bounds) >= bredest * 2)
      .sort((a, b) => a.year! - b.year!)[0];
    if (!næste) break;
    under.push(næste);
    bredest = areal(næste.bounds);
  }

  return { style: pastSheetStyle(sheet, [...under.reverse(), øverst]), label: String(øverst.year) };
}
